import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Brain, Plus, Save, FileText, History } from "lucide-react";
import { toast } from "sonner";
import { formatUKDate } from "@/lib/dateUtils";

interface CaseFormulationProps {
  clientId: string;
  clinicianId: string;
}

const FORMULATION_TYPES = [
  { value: 'cbt', label: 'CBT (5 Areas Model)' },
  { value: 'psychodynamic', label: 'Psychodynamic' },
  { value: 'systemic', label: 'Systemic' },
  { value: 'integrative', label: 'Integrative' },
  { value: 'person_centred', label: 'Person-Centred' },
  { value: 'act', label: 'ACT' },
  { value: 'dbt', label: 'DBT' },
  { value: 'custom', label: 'Custom' }
];

export function CaseFormulation({ clientId, clinicianId }: CaseFormulationProps) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [formulation, setFormulation] = useState({
    formulation_type: 'cbt',
    presenting_problems: [''],
    predisposing_factors: '',
    precipitating_factors: '',
    perpetuating_factors: '',
    protective_factors: '',
    core_beliefs: '',
    early_experiences: '',
    behavioural_patterns: '',
    emotional_patterns: '',
    cognitive_patterns: '',
    relational_patterns: '',
    treatment_goals: [''],
    intervention_plan: ''
  });

  // Fetch formulations
  const { data: formulations, isLoading } = useQuery({
    queryKey: ['case-formulations', clientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('case_formulations')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    }
  });

  // Save formulation
  const saveMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('case_formulations')
        .insert({
          client_id: clientId,
          clinician_id: clinicianId,
          ...formulation,
          presenting_problems: formulation.presenting_problems.filter(p => p.trim()),
          treatment_goals: formulation.treatment_goals.filter(g => g.trim()),
          status: 'draft'
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case-formulations', clientId] });
      setIsEditing(false);
      toast.success("Case formulation saved");
    },
    onError: () => {
      toast.error("Failed to save formulation");
    }
  });

  const activeFormulation = formulations?.find(f => f.status === 'active') || formulations?.[0];

  const addProblem = () => {
    setFormulation({
      ...formulation,
      presenting_problems: [...formulation.presenting_problems, '']
    });
  };

  const updateProblem = (index: number, value: string) => {
    const newProblems = [...formulation.presenting_problems];
    newProblems[index] = value;
    setFormulation({ ...formulation, presenting_problems: newProblems });
  };

  const addGoal = () => {
    setFormulation({
      ...formulation,
      treatment_goals: [...formulation.treatment_goals, '']
    });
  };

  const updateGoal = (index: number, value: string) => {
    const newGoals = [...formulation.treatment_goals];
    newGoals[index] = value;
    setFormulation({ ...formulation, treatment_goals: newGoals });
  };

  if (isEditing) {
    return (
      <Card className="bg-card/50 backdrop-blur-sm border-border/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            New Case Formulation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium">Formulation Model</label>
            <Select
              value={formulation.formulation_type}
              onValueChange={(value) => setFormulation({ ...formulation, formulation_type: value })}
            >
              <SelectTrigger className="bg-background/50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FORMULATION_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Presenting Problems */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Presenting Problems</label>
              <Button size="sm" variant="ghost" onClick={addProblem}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {formulation.presenting_problems.map((problem, idx) => (
              <Input
                key={idx}
                value={problem}
                onChange={(e) => updateProblem(idx, e.target.value)}
                placeholder={`Problem ${idx + 1}`}
                className="bg-background/50"
              />
            ))}
          </div>

          {/* 4 P's */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Predisposing Factors</label>
              <Textarea
                value={formulation.predisposing_factors}
                onChange={(e) => setFormulation({ ...formulation, predisposing_factors: e.target.value })}
                placeholder="Early experiences, genetics, attachment history..."
                className="bg-background/50 min-h-24"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Precipitating Factors</label>
              <Textarea
                value={formulation.precipitating_factors}
                onChange={(e) => setFormulation({ ...formulation, precipitating_factors: e.target.value })}
                placeholder="Recent triggers, life events..."
                className="bg-background/50 min-h-24"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Perpetuating Factors</label>
              <Textarea
                value={formulation.perpetuating_factors}
                onChange={(e) => setFormulation({ ...formulation, perpetuating_factors: e.target.value })}
                placeholder="Maintaining behaviours, avoidance patterns..."
                className="bg-background/50 min-h-24"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Protective Factors</label>
              <Textarea
                value={formulation.protective_factors}
                onChange={(e) => setFormulation({ ...formulation, protective_factors: e.target.value })}
                placeholder="Strengths, support systems, resilience..."
                className="bg-background/50 min-h-24"
              />
            </div>
          </div>

          {/* Core Beliefs & Patterns */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Core Beliefs</label>
              <Textarea
                value={formulation.core_beliefs}
                onChange={(e) => setFormulation({ ...formulation, core_beliefs: e.target.value })}
                placeholder="Deeply held beliefs about self, others, world..."
                className="bg-background/50"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Cognitive Patterns</label>
                <Textarea
                  value={formulation.cognitive_patterns}
                  onChange={(e) => setFormulation({ ...formulation, cognitive_patterns: e.target.value })}
                  placeholder="Thinking styles, distortions..."
                  className="bg-background/50"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Emotional Patterns</label>
                <Textarea
                  value={formulation.emotional_patterns}
                  onChange={(e) => setFormulation({ ...formulation, emotional_patterns: e.target.value })}
                  placeholder="Emotional regulation, typical responses..."
                  className="bg-background/50"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Behavioural Patterns</label>
                <Textarea
                  value={formulation.behavioural_patterns}
                  onChange={(e) => setFormulation({ ...formulation, behavioural_patterns: e.target.value })}
                  placeholder="Coping behaviours, avoidance..."
                  className="bg-background/50"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Relational Patterns</label>
                <Textarea
                  value={formulation.relational_patterns}
                  onChange={(e) => setFormulation({ ...formulation, relational_patterns: e.target.value })}
                  placeholder="Attachment style, interpersonal dynamics..."
                  className="bg-background/50"
                />
              </div>
            </div>
          </div>

          {/* Treatment Goals */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Treatment Goals</label>
              <Button size="sm" variant="ghost" onClick={addGoal}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {formulation.treatment_goals.map((goal, idx) => (
              <Input
                key={idx}
                value={goal}
                onChange={(e) => updateGoal(idx, e.target.value)}
                placeholder={`Goal ${idx + 1}`}
                className="bg-background/50"
              />
            ))}
          </div>

          {/* Intervention Plan */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Intervention Plan</label>
            <Textarea
              value={formulation.intervention_plan}
              onChange={(e) => setFormulation({ ...formulation, intervention_plan: e.target.value })}
              placeholder="Therapeutic approach, techniques, session structure..."
              className="bg-background/50 min-h-32"
            />
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setIsEditing(false)}>
              Cancel
            </Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              <Save className="h-4 w-4 mr-1" />
              Save Formulation
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card/50 backdrop-blur-sm border-border/30">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            Case Formulation
          </CardTitle>
          <Button size="sm" onClick={() => setIsEditing(true)}>
            <Plus className="h-4 w-4 mr-1" />
            New Formulation
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-muted-foreground text-sm">Loading formulations...</p>
        ) : !activeFormulation ? (
          <div className="text-center py-8">
            <Brain className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground">No case formulation yet</p>
            <p className="text-sm text-muted-foreground mb-4">
              Create a comprehensive psychological formulation for this client
            </p>
            <Button onClick={() => setIsEditing(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Create Formulation
            </Button>
          </div>
        ) : (
          <Tabs defaultValue="current">
            <TabsList className="mb-4">
              <TabsTrigger value="current">Current Formulation</TabsTrigger>
              <TabsTrigger value="history">
                <History className="h-4 w-4 mr-1" />
                History ({formulations?.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="current" className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Badge variant="outline">
                  {FORMULATION_TYPES.find(t => t.value === activeFormulation.formulation_type)?.label}
                </Badge>
                <Badge className={activeFormulation.status === 'active' ? 'bg-green-500/20 text-green-700' : 'bg-muted'}>
                  {activeFormulation.status}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  Created: {formatUKDate(new Date(activeFormulation.created_at))}
                </span>
              </div>

              {activeFormulation.presenting_problems?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Presenting Problems</h4>
                  <ul className="list-disc list-inside text-sm text-muted-foreground">
                    {activeFormulation.presenting_problems.map((problem: string, idx: number) => (
                      <li key={idx}>{problem}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {activeFormulation.predisposing_factors && (
                  <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                    <h4 className="text-sm font-medium mb-1">Predisposing Factors</h4>
                    <p className="text-sm text-muted-foreground">{activeFormulation.predisposing_factors}</p>
                  </div>
                )}
                {activeFormulation.precipitating_factors && (
                  <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                    <h4 className="text-sm font-medium mb-1">Precipitating Factors</h4>
                    <p className="text-sm text-muted-foreground">{activeFormulation.precipitating_factors}</p>
                  </div>
                )}
                {activeFormulation.perpetuating_factors && (
                  <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                    <h4 className="text-sm font-medium mb-1">Perpetuating Factors</h4>
                    <p className="text-sm text-muted-foreground">{activeFormulation.perpetuating_factors}</p>
                  </div>
                )}
                {activeFormulation.protective_factors && (
                  <div className="p-3 rounded-lg bg-muted/30 border border-border/30">
                    <h4 className="text-sm font-medium mb-1">Protective Factors</h4>
                    <p className="text-sm text-muted-foreground">{activeFormulation.protective_factors}</p>
                  </div>
                )}
              </div>

              {activeFormulation.core_beliefs && (
                <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                  <h4 className="text-sm font-medium mb-1">Core Beliefs</h4>
                  <p className="text-sm">{activeFormulation.core_beliefs}</p>
                </div>
              )}

              {activeFormulation.treatment_goals?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Treatment Goals</h4>
                  <ul className="list-decimal list-inside text-sm text-muted-foreground">
                    {activeFormulation.treatment_goals.map((goal: string, idx: number) => (
                      <li key={idx}>{goal}</li>
                    ))}
                  </ul>
                </div>
              )}

              {activeFormulation.intervention_plan && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Intervention Plan</h4>
                  <p className="text-sm text-muted-foreground">{activeFormulation.intervention_plan}</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="history" className="space-y-3">
              {formulations?.map(f => (
                <div
                  key={f.id}
                  className="p-3 rounded-lg bg-muted/30 border border-border/30 flex items-center justify-between"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">
                        {FORMULATION_TYPES.find(t => t.value === f.formulation_type)?.label}
                      </Badge>
                      <Badge className={f.status === 'active' ? 'bg-green-500/20 text-green-700' : 'bg-muted'}>
                        {f.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Version {f.version} • {formatUKDate(new Date(f.created_at))}
                    </p>
                  </div>
                  <Button size="sm" variant="ghost">
                    <FileText className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
}
